<template>
  <div class="media-planning-campaign-dialog">
    <!-- Temporarily disabled portal to test if it's causing input issues -->
    <!-- <portal to="base-modal"> -->
      <base-modal @closed_requested="close_dialog">
        <form @submit.prevent="save" ref="form">
          <h3>
            <template v-if="campaign.id">Update campaign</template>
            <template v-else>Add campaign</template>
            <a @click.stop.prevent="copy_campaign_id" title="Copy campaign ID">
              <i class="fi-clipboard"></i>
            </a>
            <time-tracking type="setup" :record="campaign" :init_start="!campaign.id && false" ref="time_tracking"></time-tracking>
          </h3>

          <div v-if="errors" class="callout alert">
            <h5>Error</h5>
            <p v-for="error in errors">{{ error }}</p>
            <button @click.stop="errors = null" class="close-button" aria-label="Dismiss alert" type="button">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <fieldset>
            <template v-for="integration in integrations">
              <input type="radio"
                     name="new_campaign_channel"
                     :value="integration.channel_id"
                     :id="`new_campaign_${integration.channel_id}`"
                     v-model="campaign.channel_id"
                     :disabled="campaign.id || is_loading">
              <label :for="`new_campaign_${integration.channel_id}`"
                     class="media-planning-campaign-dialog--radio-label">
                <channel-icon :channel_title="integration.channel_title"></channel-icon>
                {{ integration.channel_title }}
              </label>
            </template>
            <label>
              Title:
              <input type="text" v-model="campaign.title" :disabled="is_loading">
            </label>
          </fieldset>

          <div class="media-planning-campaign-dialog--grid5 media-planning-campaign-dialog--callout">
            <template v-for="(flight, index) in campaign.flights">
              <div class="media-planning-campaign-dialog--cell">
                <vue-ctk-date-time-picker id="media_plan_start_date"
                                          format="YYYY-MM-DD"
                                          hint="Start date *"
                                          color="#36bbb1"
                                          button-color="#36bbb1"
                                          button-now-translation="Jetzt!"
                                          :only-date="true"
                                          :no-button-now="true"
                                          label="Erster Tag"
                                          formatted="ll"
                                          :disabledDates="disabled_dates(flight)"
                                          :error="!flight.start_date && flight.active_reporting"
                                          :disabled="is_loading"
                                          v-model="flight.start_date"/>
              </div>
              <div class="media-planning-campaign-dialog--cell">
                <vue-ctk-date-time-picker id="media_plan_end_date"
                                          format="YYYY-MM-DD"
                                          hint="End date *"
                                          color="#36bbb1"
                                          button-color="#36bbb1"
                                          button-now-translation="Jetzt!"
                                          :only-date="true"
                                          :no-button-now="true"
                                          label="Letzter Tag"
                                          formatted="ll"
                                          :disabledDates="disabled_dates(flight)"
                                          :min-date="flight.start_date"
                                          :error="end_date_error(flight)"
                                          :disabled="is_loading"
                                          v-model="flight.end_date"/>
              </div>
              <div class="media-planning-campaign-dialog--cell">
                <toggle-button :state="flight.active_reporting"
                               :disabled="is_loading"
                               style="margin-top: 0.5em"
                               text="Activate reporting"
                               @toggled="toggle_active_reporting(index)">
                </toggle-button>
              </div>

              <div class="media-planning-campaign-dialog--cell">
                <vue-numeric v-model="flight.flat_fee"
                             separator="."
                             decimal-separator=","
                             :precision=2
                             currency="Euro"
                             currency-symbol-position="suffix"
                             :disabled="campaign.target_type !== 'budget' || is_loading">
                </vue-numeric>
              </div>

              <div class="media-planning-campaign-dialog--cell">
                <div class="text-right float-right">
                  <button @click.prevent="remove_flight(index)"
                          class="media-planning-campaign-dialog--button--alert"
                          style="margin-bottom: 0; margin-top: 0.5em"
                          :disabled="is_loading"
                          :class="disabled_button_class">
                    <i class="fi-x"></i>
                  </button>
                </div>
              </div>
            </template>

            <div class="media-planning-campaign-dialog--cell">
              <button v-if="flights_configured"
                      @click.prevent="add_flight"
                      class="media-planning-campaign-dialog--button"
                      style="text-align: right;"
                      :disabled="is_loading"
                      :class="disabled_button_class">
                <i class="fi-plus"></i>
                Flight hinzufügen
              </button>
            </div>
          </div>

          <div class="media-planning-campaign-dialog--callout">
            <fieldset>
              <label>Target:</label>

              <div style="margin-left: 0">
                <input type="radio"
                       name="campaign_target_type"
                       :value="null"
                       :id="`campaign_target_type_null`"
                       :disabled="is_loading"
                       v-model="campaign.target_type">
                <label :for="`campaign_target_type_null`">
                  w/o
                </label>

                <input type="radio"
                       name="campaign_target_type"
                       value="impressions"
                       :id="`campaign_target_type_impressions`"
                       :disabled="is_loading"
                       v-model="campaign.target_type">
                <label :for="`campaign_target_type_impressions`">
                  Impressions
                </label>

                <input type="radio"
                       name="campaign_target_type"
                       value="clicks"
                       :id="`campaign_target_type_clicks`"
                       :disabled="is_loading"
                       v-model="campaign.target_type">
                <label :for="`campaign_target_type_clicks`">
                  Clicks
                </label>

                <input type="radio"
                       name="campaign_target_type"
                       value="budget"
                       :id="`campaign_target_type_budget`"
                       :disabled="is_loading"
                       v-model="campaign.target_type">
                <label :for="`campaign_target_type_budget`">
                  Budget
                </label>

                <input type="checkbox"
                       name="campaign_apply_budget_cap"
                       :value="campaign.apply_budget_cap"
                       :id="`campaign_apply_budget_cap`"
                       v-model="campaign.apply_budget_cap"
                       :disabled="campaign.target_type === null || campaign.dummy || is_loading">
                <label for="campaign_apply_budget_cap" class="media-planning-campaign-dialog--checkbox-label">
                  Apply budget cap
                </label>

                <template v-if="campaign.target_type === 'budget'">
                  <input type="radio"
                         name="campaign_budget_target_type"
                         value="impressions"
                         id="campaign_budget_target_type_impressions"
                         v-model="campaign.budget_target_type"
                         @click="toggle_budget_target_type"
                         :disabled="is_loading">
                  <label for="campaign_budget_target_type_impressions">
                    Impressions
                  </label>

                  <input type="radio"
                         name="campaign_budget_target_type"
                         value="clicks"
                         id="campaign_budget_target_type_clicks"
                         v-model="campaign.budget_target_type"
                         @click="toggle_budget_target_type"
                         :disabled="is_loading">
                  <label for="campaign_budget_target_type_clicks">
                    Clicks
                  </label>

                  <label style="margin-left: 0">
                    <span v-if="campaign.budget_target_type === 'impressions'">Sum impressions</span>
                    <span v-else-if="campaign.budget_target_type === 'clicks'">Sum clicks</span>
                    <vue-numeric v-if="campaign.budget_target_type"
                                 v-model="campaign.budget_target_value"
                                 id="campaign_budget_target_value"
                                 :min="1"
                                 separator="."
                                 :disabled="is_loading">
                    </vue-numeric>
                  </label>
                </template>
              </div>

              <label style="margin-left: 0">
                <span v-if="campaign.target_type === 'impressions'">Sum impressions</span>
                <span v-else-if="campaign.target_type === 'clicks'">Sum clicks</span>
                <vue-numeric v-if="campaign.target_type && campaign.target_type !== 'budget'"
                             v-model="campaign.target_value"
                             :min="1"
                             separator="."
                             :disabled="is_loading">
                </vue-numeric>
              </label>
            </fieldset>
          </div>

          <div class="media-planning-campaign-dialog--callout media-planning-campaign-dialog--grid2">
            <fieldset class="media-planning-campaign-dialog--cell" style="margin-left: 0">
              <label>Turnover type:</label>

              <input type="radio"
                     name="campaign_turnover_type"
                     :value="null"
                     :id="`campaign_turnover_type_null`"
                     v-model="campaign.turnover_type"
                     disabled="disabled">
              <label :for="`campaign_turnover_type_null`">
                w/o
              </label>

              <input type="radio"
                     name="campaign_turnover_type"
                     value="tkp"
                     :id="`campaign_turnover_type_tkp`"
                     v-model="campaign.turnover_type"
                     :disabled="campaign.target_type !== 'impressions' || is_loading">
              <label :for="`campaign_turnover_type_tkp`">
                <abbr title="Tausend-Kontakt-Preis">TKP</abbr>
              </label>

              <input type="radio"
                     name="campaign_turnover_type"
                     value="cpc"
                     :id="`campaign_turnover_type_cpc`"
                     v-model="campaign.turnover_type"
                     :disabled="campaign.target_type !== 'clicks' || is_loading">
              <label :for="`campaign_turnover_type_cpc`">
                <abbr title="Cost-per-Click">CPC</abbr>
              </label>

              <input type="radio"
                     name="campaign_turnover_type"
                     value="hf"
                     id="campaign_turnover_type_hf"
                     v-model="campaign.turnover_type"
                     :disabled="is_loading">
              <label :for="`campaign_turnover_type_hf`">
                <abbr title="Handling fee: turnover = cost * (1.0 + hf)">HF</abbr>
              </label>

              <input type="radio"
                     name="campaign_turnover_type"
                     value="ghf"
                     id="campaign_turnover_type_ghf"
                     v-model="campaign.turnover_type"
                     :disabled="is_loading">
              <label :for="`campaign_turnover_type_ghf`">
                <abbr title="Gross handling fee: turnover = cost / (1.0 - hf)">GHF</abbr>
              </label>

              <input type="radio"
                     name="campaign_turnover_type"
                     value="natural_discount"
                     id="campaign_turnover_type_natural_discount"
                     :disabled="campaign.target_type === 'budget' || is_loading"
                     v-model="campaign.turnover_type">
              <label :for="`campaign_turnover_type_natural_discount`">
                <abbr title="Naturalrabatt">NR</abbr>
              </label>

              <div class="media-planning-campaign-dialog--grid4">
                <label v-if="campaign.turnover_type && campaign.turnover_type !== 'natural_discount'" class="media-planning-campaign-dialog--cell"
                       style="margin-left: 0">
                  <span style="white-space: nowrap; text-overflow: ellipsis; display: inline-block;">{{ turnover_type_label }}</span>
                  <vue-numeric v-model="campaign.turnover_value"
                               :min="min_turnover_value"
                               separator="."
                               decimal-separator=","
                               :precision=2
                               :currency="['hf', 'ghf'].includes(campaign.turnover_type) ? '%' : 'Euro'"
                               currency-symbol-position="suffix"
                               :disabled="is_loading">
                  </vue-numeric>
                </label>

                <label v-if="campaign.turnover_type && campaign.turnover_type !== 'natural_discount'"
                       class="media-planning-campaign-dialog--cell" style="margin-left: 0">
                  One-off fee
                  <vue-numeric v-model="campaign.turnover_one_off_fee"
                               :min="null"
                               separator="."
                               decimal-separator=","
                               :precision=2
                               currency="Euro"
                               currency-symbol-position="suffix"
                               :disabled="[null, 'natural_discount'].includes(campaign.turnover_type) || is_loading">
                  </vue-numeric>
                </label>

                <label v-if="campaign.turnover_type && campaign.turnover_type !== 'natural_discount'"
                       class="media-planning-campaign-dialog--cell" style="margin-left: 0">
                  Budget
                  <i v-if="!campaign.apply_budget_cap" class="fi-asterisk" title="Budget cap not applied"></i>
                  <vue-numeric v-model="campaign.turnover_budget"
                               :min="null"
                               separator="."
                               decimal-separator=","
                               :precision=2
                               currency="Euro"
                               currency-symbol-position="suffix"
                               :disabled="[null, 'natural_discount'].includes(campaign.turnover_type) || is_loading">
                  </vue-numeric>
                </label>

                <label v-if="campaign.turnover_type && campaign.turnover_type !== 'natural_discount'"
                       class="media-planning-campaign-dialog--cell" style="margin-left: 0">
                  Budget type
                  <select id="turnover_budget_type"
                          v-model="campaign.turnover_budget_type"
                          :disabled="[null, 'natural_discount'].includes(campaign.turnover_type) || !campaign.turnover_budget || is_loading">
                    <option v-if="!campaign.turnover_budget" :value="null">w/o</option>
                    <option value="total">Total budget</option>
                    <option value="monthly">Monthly budget</option>
                    <option value="daily">Daily budget</option>
                  </select>
                </label>
              </div>
            </fieldset>

            <fieldset v-if="campaign.dummy" class="media-planning-campaign-dialog--cell">
              <label>Cost type <small>(Dummy campaigns)</small>:</label>

              <input type="radio"
                     name="campaign_cost_type"
                     :value="null"
                     :id="`campaign_cost_type_null`"
                     :disabled="is_loading"
                     v-model="campaign.cost_type">
              <label :for="`campaign_cost_type_null`">
                w/o
              </label>

              <input type="radio"
                     name="campaign_cost_type"
                     value="tkp"
                     :id="`campaign_cost_type_tkp`"
                     v-model="campaign.cost_type"
                     :disabled="campaign.target_type !== 'impressions' || is_loading">
              <label :for="`campaign_cost_type_tkp`">
                <abbr title="Tausend-Kontakt-Preis">TKP</abbr>
              </label>

              <input type="radio"
                     name="campaign_cost_type"
                     value="cpc"
                     :id="`campaign_cost_type_cpc`"
                     v-model="campaign.cost_type"
                     :disabled="campaign.target_type !== 'clicks' || is_loading">
              <label :for="`campaign_cost_type_cpc`">
                <abbr title="Cost-per-Click">CPC</abbr>
              </label>

              <label v-if="campaign.cost_type">
                Preis
                <vue-numeric v-model="campaign.cost_value"
                             :min="0.01"
                             separator="."
                             :precision=2
                             currency="Euro"
                             currency-symbol-position="suffix"
                             :disabled="is_loading">
                </vue-numeric>
              </label>
            </fieldset>
          </div>

          <div class="media-planning-campaign-dialog--callout">
            <fieldset>
              <label><abbr title="Key-Performance-Indicator">KPIs</abbr>:</label>

              <input type="checkbox"
                     name="campaign_include_clicks"
                     :value="campaign.include_clicks"
                     :id="`campaign_include_clicks`"
                     v-model="campaign.include_clicks"
                     :disabled="campaign.dummy || (channel && !channel.supports_clicks) || is_loading">
              <label :for="`campaign_include_clicks`"
                     class="media-planning-campaign-dialog--checkbox-label">
                Clicks
              </label>

              <input type="checkbox"
                     name="campaign_include_complete"
                     :value="campaign.include_complete"
                     :id="`campaign_include_complete`"
                     v-model="campaign.include_complete"
                     :disabled="campaign.dummy || (channel && !channel.supports_complete) || is_loading">
              <label :for="`campaign_include_complete`"
                     class="media-planning-campaign-dialog--checkbox-label">
                Complete
              </label>

              <input type="checkbox"
                     name="campaign_include_video_views"
                     :value="campaign.include_video_views"
                     :id="`campaign_include_video_views`"
                     v-model="campaign.include_video_views"
                     :disabled="campaign.dummy || (channel && !channel.supports_video_views) || is_loading">
              <label :for="`campaign_include_video_views`"
                     class="media-planning-campaign-dialog--checkbox-label">
                Video views
              </label>

              <input type="checkbox"
                     name="campaign_include_conversions"
                     :value="campaign.include_conversions"
                     :id="`campaign_include_conversions`"
                     v-model="campaign.include_conversions"
                     :disabled="campaign.dummy || (channel && !channel.supports_conversions) || is_loading">
              <label :for="`campaign_include_conversions`"
                     class="media-planning-campaign-dialog--checkbox-label">
                Conversions
              </label>

              <input type="checkbox"
                     name="campaign_include_cpm"
                     :value="campaign.include_cpm"
                     :id="`campaign_include_cpm`"
                     v-model="campaign.include_cpm"
                     :disabled="campaign.dummy || (channel && !channel.supports_cpm) || is_loading">
              <label :for="`campaign_include_cpm`"
                     class="media-planning-campaign-dialog--checkbox-label">
                <abbr title="Cost per mille">CPM</abbr>
              </label>

              <input type="checkbox"
                     name="campaign_include_cpc"
                     :value="campaign.include_cpc"
                     :id="`campaign_include_cpc`"
                     v-model="campaign.include_cpc"
                     :disabled="campaign.dummy || (channel && !channel.supports_cpc) || is_loading">
              <label :for="`campaign_include_cpc`"
                     class="media-planning-campaign-dialog--checkbox-label">
                <abbr title="Cost per click">CPC</abbr>
              </label>

              <input type="checkbox"
                     name="campaign_include_cpo"
                     :value="campaign.include_cpo"
                     :id="`campaign_include_cpo`"
                     v-model="campaign.include_cpo"
                     :disabled="campaign.dummy || (channel && !channel.supports_cpo) || is_loading">
              <label :for="`campaign_include_cpo`"
                     class="media-planning-campaign-dialog--checkbox-label">
                <abbr title="Cost per conversion">CPO</abbr>
              </label>

              <input type="checkbox"
                     name="campaign_include_ad_group_metrics"
                     :value="campaign.include_ad_group_metrics"
                     :id="`campaign_include_ad_group_metrics`"
                     v-model="campaign.include_ad_group_metrics"
                     :disabled="campaign.dummy || (channel && !channel.supports_ad_group_metrics) || is_loading">
              <label :for="`campaign_include_ad_group_metrics`"
                     class="media-planning-campaign-dialog--checkbox-label">
                <abbr title="Ad group metrics">AGM</abbr>
              </label>

              <input type="checkbox"
                     name="campaign_include_keyword_metrics"
                     :value="campaign.include_keyword_metrics"
                     :id="`campaign_include_keyword_metrics`"
                     v-model="campaign.include_keyword_metrics"
                     :disabled="campaign.dummy || (channel && !channel.supports_keyword_metrics) || is_loading">
              <label :for="`campaign_include_keyword_metrics`"
                     class="media-planning-campaign-dialog--checkbox-label">
                <abbr title="Keyword metrics">KWM</abbr>
              </label>
            </fieldset>
          </div>

          <div class="media-planning-campaign-dialog--callout">
            <fieldset>
              <label>Dummy campaign:</label>

              <input type="checkbox"
                     name="campaign_dummy"
                     :value="null"
                     :id="`campaign_dummy`"
                     v-model="campaign.dummy"
                     :disabled="dummy_disabled">
              <label :for="`campaign_dummy`">
                Campaigns w/o integrations
              </label>
            </fieldset>
          </div>

          <div class="media-planning-campaign-dialog--callout">
            <fieldset>
              <label>Reported costs:</label>

              <input type="checkbox"
                     name="campaign_exclude_reported_costs"
                     :value="campaign.exclude_reported_costs"
                     :id="`campaign_exclude_reported_costs`"
                     v-model="campaign.exclude_reported_costs"
                     :disabled="is_loading">
              <label :for="`campaign_exclude_reported_costs`">
                Set reported costs to 0 (e.g. Added Value)
              </label>
            </fieldset>
          </div>

          <div class="media-planning-campaign-dialog--callout">
            <fieldset>
              <label for="xv_order_id">crossvertise order ID</label>
              <div class="input-group">
                <input type="text"
                       v-model="campaign.xv_order_id"
                       id="xv_order_id"
                       class="input-group-field"
                       :disabled="is_loading">
                <div class="input-group-button">
                  <button @click.prevent="delete_xv_order_id" type="submit"
                          class="button media-planning-campaign-dialog--button--alert" :disabled="is_loading">
                    <i class="fi-trash"></i>
                  </button>
                </div>
              </div>
            </fieldset>
          </div>

          <div>
            <button v-if="campaign.id"
                    @click.stop.prevent="delete_campaign"
                    :class="disabled_button_class"
                    :disabled="is_loading"
                    class="media-planning-campaign-dialog--button--alert">
              <i class="fi-alert"></i>
              Löschen ...
            </button>
            <button @click="close_dialog"
                    :disabled="is_loading"
                    class="media-planning-campaign-dialog--button--abort">
              <i class="fi-x"></i>
              Abbrechen
            </button>
            <button v-if="campaign_configured"
                    :disabled="is_loading"
                    class="media-planning-campaign-dialog--button"
                    :class="disabled_button_class">
              <i class="fi-save"></i>
              Campaign
            </button>
          </div>
        </form>
      </base-modal>
    <!-- </portal> -->
  </div>
</template>

<script>
import "./media-planning-campaign-dialog.scss"
import BaseModal from "../base-modal/base-modal-app"
import VueCtkDateTimePicker from '../../lib/vue-ctk-date-time-picker.common'
import '../../lib/vue-ctk-date-time-picker.css'
import BooleanIcon from '../boolean-icon/boolean-icon'
import ToggleButton from "../toggle-button/toggle-button"
import ChannelIcon from "../channel-icon/channel-icon-app"
import axios from "axios"
import moment from "moment"

moment.locale("de");
import VueNumeric from 'vue-numeric'
import Vue from 'vue/dist/vue.esm'
import TimeTracking from "../time-tracking/time-tracking-app.vue";

Vue.component('vue-ctk-date-time-picker', VueCtkDateTimePicker)

export default {
  name:       "media-planning-campaign-dialog",
  props:      {
    media_planning: {
      required: true,
    },
    publishers: {
      required: true,
    },
    campaign:       {
      required: false,
      default() {
        return {
          id:                       null,
          channel_id:               null,
          title:                    "",
          flights:                  [{
            start_date:       null,
            end_date:         null,
            active_reporting: false,
            flat_fee:         0,
          }],
          target_type:              null,
          target_value:             null,
          apply_budget_cap:         true,
          budget_target_type:       null,
          budget_target_value:      null,
          turnover_type:            null,
          turnover_value:           10.0,
          turnover_budget:          null,
          turnover_budget_type:     null,
          turnover_one_off_fee:     null,
          cost_type:                null,
          cost_value:               null,
          include_clicks:           true,
          include_complete:         true,
          include_video_views:      true,
          include_conversions:      true,
          include_cpm:              true,
          include_cpc:              true,
          include_cpo:              true,
          include_ad_group_metrics: true,
          include_keyword_metrics:  true,
          exclude_reported_costs:   false,
          dummy:                    false,
          integrations:             [],
          xv_order_id:              null,
        }
      },
    },
    integrations:   {
      required: true,
    },
    channels:       {
      required: true,
    },
  },
  components: {
    TimeTracking,
    BaseModal,
    VueCtkDateTimePicker,
    BooleanIcon,
    ToggleButton,
    ChannelIcon,
    VueNumeric,
  },
  data() {
    return {
      xv_order_id: this.campaign.xv_order_id,
      is_loading: false,
      errors: null,
    }
  },
  created() {
    // Normalize null values to prevent VueNumeric warnings
    // VueNumeric expects Number or String, not null
    if (this.campaign.target_value === null) this.campaign.target_value = 0
    if (this.campaign.budget_target_value === null) this.campaign.budget_target_value = 0
    if (this.campaign.turnover_one_off_fee === null) this.campaign.turnover_one_off_fee = 0
    if (this.campaign.turnover_budget === null) this.campaign.turnover_budget = 0
    if (this.campaign.cost_value === null) this.campaign.cost_value = 0

    // Normalize flat_fee in all flights
    if (this.campaign.flights) {
      this.campaign.flights.forEach(flight => {
        if (flight.flat_fee === null || flight.flat_fee === undefined) {
          flight.flat_fee = 0
        }
      })
    }
  },
  methods:  {
    toggle_active_reporting(index) {
      const flight = this.campaign.flights[index];
      flight.active_reporting = !flight.active_reporting
    },
    add_flight() {
      const last_flight = this.campaign.flights[this.campaign.flights.length - 1];
      const flat_fee = last_flight ? last_flight.flat_fee : 0;
      this.campaign.flights.push({ start_date: null, end_date: null, active_reporting: false, flat_fee: flat_fee })
    },
    save() {
      if (this.is_loading) return
      if (!this.campaign_configured) return

      this.is_loading = true
      this.errors = null

      if (this.campaign.id) {
        axios({
          method:  'post',
          url:     `/media_plannings/${this.media_planning.id}/update_campaign`,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          data:    {
            campaign_id:              this.campaign.id,
            channel_id:               this.campaign.channel_id,
            title:                    this.campaign.title,
            flights:                  this.campaign.flights,
            target_type:              this.campaign.target_type,
            target_value:             this.campaign.target_value,
            apply_budget_cap:         this.campaign.apply_budget_cap,
            budget_target_type:       this.campaign.budget_target_type,
            budget_target_value:      this.campaign.budget_target_value,
            turnover_type:            this.campaign.turnover_type,
            turnover_value:           this.campaign.turnover_value,
            turnover_budget:          this.campaign.turnover_budget || null,
            turnover_budget_type:     this.campaign.turnover_budget_type || null,
            turnover_one_off_fee:     this.campaign.turnover_one_off_fee || null,
            cost_type:                this.campaign.cost_type,
            cost_value:               this.campaign.cost_value,
            include_clicks:           this.campaign.include_clicks,
            include_complete:         this.campaign.include_complete,
            include_video_views:      this.campaign.include_video_views,
            include_conversions:      this.campaign.include_conversions,
            include_cpm:              this.campaign.include_cpm,
            include_cpc:              this.campaign.include_cpc,
            include_cpo:              this.campaign.include_cpo,
            include_ad_group_metrics: this.campaign.include_ad_group_metrics,
            include_keyword_metrics:  this.campaign.include_keyword_metrics,
            exclude_reported_costs:   this.campaign.exclude_reported_costs,
            dummy:                    this.campaign.dummy,
            xv_order_id:              this.campaign.xv_order_id || null,
          },
        }).then(_response => {
          this.is_loading = false
          this.close_dialog()
        }).catch(error => {
          this.is_loading = false
          this.errors = error.response.data.errors.map(error => error.title)
          console.log("ERROR", error.response.data)
          alert("Error saving campaign")
        })
      } else {
        axios({
          method:  'post',
          url:     `/media_plannings/${this.media_planning.id}/add_campaign`,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          data:    {
            channel_id:               this.campaign.channel_id,
            title:                    this.campaign.title,
            flights:                  this.campaign.flights,
            target_type:              this.campaign.target_type,
            target_value:             this.campaign.target_value,
            apply_budget_cap:         this.campaign.apply_budget_cap,
            budget_target_type:       this.campaign.budget_target_type,
            budget_target_value:      this.campaign.budget_target_value,
            turnover_type:            this.campaign.turnover_type,
            turnover_value:           this.campaign.turnover_value,
            turnover_budget:          this.campaign.turnover_budget || null,
            turnover_budget_type:     this.campaign.turnover_budget_type || null,
            turnover_one_off_fee:     this.campaign.turnover_one_off_fee || null,
            cost_type:                this.campaign.cost_type,
            cost_value:               this.campaign.cost_value,
            include_clicks:           this.campaign.include_clicks,
            include_complete:         this.campaign.include_complete,
            include_video_views:      this.campaign.include_video_views,
            include_conversions:      this.campaign.include_conversions,
            include_cpm:              this.campaign.include_cpm,
            include_cpc:              this.campaign.include_cpc,
            include_cpo:              this.campaign.include_cpo,
            include_ad_group_metrics: this.campaign.include_ad_group_metrics,
            include_keyword_metrics:  this.campaign.include_keyword_metrics,
            exclude_reported_costs:   this.campaign.exclude_reported_costs,
            dummy:                    this.campaign.dummy,
            time_tracking:            this.$refs.time_tracking && this.$refs.time_tracking.data ? this.$refs.time_tracking.data() : null,
            xv_order_id:              this.campaign.xv_order_id || null,
          },
        }).then(_response => {
          this.is_loading = false
          this.close_dialog()
        }).catch(error => {
          this.is_loading = false
          this.errors = error.response.data.errors.map(error => error.title)
          console.log("ERROR", error.response.data)
          alert("Error saving campaign")
        })
      }
    },
    delete_campaign() {
      let confirmation = confirm("Do you really want to delete the campaign?")
      if (!confirmation) return

      this.is_loading = true
      this.errors = null

      axios({
        method:  'delete',
        url:     `/media_plannings/${this.media_planning.id}/delete_campaign`,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        data:    {
          campaign_id: this.campaign.id,
        },
      }).then((_response) => {
        this.is_loading = false
        this.close_dialog()
      }).catch(error => {
        this.is_loading = false
        this.errors = error.response.data.errors.map(error => error.title)
        console.log("ERROR", error.response.data)
        alert("Error deleting campaign")
      })
    },
    remove_flight(index) {
      this.campaign.flights.splice(index, 1);
    },
    disabled_dates(current_flight) {
      return this.campaign.flights.flatMap(flight => {
        if (flight === current_flight) return []
        const flight_dates = []
        if (!flight.end_date) return []
        let current_date = flight.start_date
        while (current_date <= flight.end_date) {
          flight_dates.push(current_date)
          current_date = moment(current_date).locale("de").add(1, "day").format("YYYY-MM-DD")
        }
        return flight_dates
      })
    },
    end_date_error(flight) {
      if (this.channel && this.channel.requires_end_date && !flight.end_date) return true
      return flight.start_date && flight.end_date && flight.start_date > flight.end_date
    },
    delete_xv_order_id() {
      this.campaign.xv_order_id = null
    },
    copy_campaign_id() {
      navigator.clipboard.writeText(this.campaign.id)
    },
    toggle_budget_target_type() {
      if (this.campaign.budget_target_type) {
        this.campaign.budget_target_type = null
      }
    },
    close_dialog() {
      if (this.$refs.time_tracking) {
        this.$refs.time_tracking.stop()
      }
      this.$emit("closed_requested")
    },
  },
  computed: {
    campaign_configured() {
      if (!this.campaign.channel_id) return
      if (!this.campaign.title) return
      if (!this.campaign.flights.length) return
      if (!this.flights_configured) return
      if (this.campaign.target_type && this.campaign.target_type !== "budget" && !this.campaign.target_value) return
      if (this.campaign.turnover_type === "tkp" || this.campaign.turnover_type === "cpc") {
        if (!this.campaign.turnover_type || !this.campaign.turnover_value) return
      }
      if (this.campaign.turnover_type === "hf" || this.campaign.turnover_type === "ghf") {
        if (!this.campaign.turnover_value && this.campaign.target_type !== "budget" && !this.campaign.turnover_one_off_fee) return
      }
      if (this.campaign.turnover_budget && !this.campaign.turnover_budget_type) return
      if (this.campaign.dummy && !this.campaign.target_type) return
      return true
    },
    flights_configured() {
      return this.campaign.flights.every(flight => {
        if (!flight.start_date) return false
        if (this.channel && this.channel.requires_end_date && !flight.end_date) return false
        return true
      })
    },
    disabled_button_class() {
      if (this.is_loading) return "media-planning-campaign-dialog--button--disabled"
      return "media-planning-campaign-dialog--button"
    },
    min_turnover_value() {
      if (this.campaign.turnover_type === "natural_discount") return 0.0
      if (this.campaign.turnover_type === "hf") return 0.0
      if (this.campaign.turnover_type === "ghf") return 0.0
      return 0.01
    },
    turnover_type_label() {
      if (this.campaign.turnover_type === "tkp") return "TKP"
      if (this.campaign.turnover_type === "cpc") return "CPC"
      if (this.campaign.turnover_type === "hf") return "Handling fee"
      if (this.campaign.turnover_type === "ghf") return "Gross handling fee"
    },
    channel() {
      return this.channels.find(channel => channel.id === this.campaign.channel_id)
    },
    dummy_disabled() {
      if (!!this.campaign.integrations.length) return true
      if (this.is_loading) return true
      if (this.campaign.flights.some(f => !f.end_date)) {
        return true
      }
      return false
    },
  },
  watch:    {
    "campaign.flights": {
      deep: true,
      handler() {
        // Auto-disable dummy mode if any flights are missing end dates
        if (this.campaign.flights.some(f => !f.end_date)) {
          this.campaign.dummy = false
        }
      }
    },
    "campaign.channel_id"() {
      if (this.channel && this.channel.requires_end_date) {
        if (this.campaign.flights.some(f => !f.end_date)) {
          this.campaign.dummy = false
        }
      }
      if (this.campaign.dummy) {
        this.campaign.turnover_budget = 0
        this.campaign.turnover_budget_type = null
        this.campaign.turnover_one_off_fee = 0
        this.campaign.include_clicks = false
        this.campaign.include_complete = false
        this.campaign.include_video_views = false
        this.campaign.include_conversions = false
        this.campaign.include_cpm = false
        this.campaign.include_cpc = false
        this.campaign.include_cpo = false
        this.campaign.include_ad_group_metrics = false
        this.campaign.include_keyword_metrics = false
      } else {
        this.campaign.include_clicks = !this.channel || this.channel.supports_clicks
        this.campaign.include_complete = !this.channel || this.channel.supports_complete
        this.campaign.include_video_views = !this.channel || this.channel.supports_video_views
        this.campaign.include_conversions = !this.channel || this.channel.supports_conversions
        this.campaign.include_cpm = !this.channel || this.channel.supports_cpm
        this.campaign.include_cpc = !this.channel || this.channel.supports_cpc
        this.campaign.include_cpo = !this.channel || this.channel.supports_cpo
        this.campaign.include_ad_group_metrics = !this.channel || this.channel.supports_ad_group_metrics
        this.campaign.include_keyword_metrics = !this.channel || this.channel.supports_keyword_metrics
      }
    },
    "campaign.target_type"() {
      if (this.campaign.target_type === null) {
        this.campaign.target_value = 0
        this.campaign.apply_budget_cap = false
        this.campaign.budget_target_type = null
        this.campaign.budget_target_value = 0
      } else {
        this.campaign.apply_budget_cap = !this.campaign.target_type.dummy
      }
      if (this.campaign.target_type === "impressions") {
        if (this.campaign.turnover_type !== "tkp" && this.campaign.turnover_type !== "hf" && this.campaign.turnover_type !== "ghf" && this.campaign.turnover_type !== "natural_discount") {
          this.campaign.turnover_type = "tkp"
          if (this.campaign.cost_type) {
            this.campaign.cost_type = "tkp"
          }
        }
      }
      if (this.campaign.target_type === "clicks") {
        if (this.campaign.turnover_type !== "cpc" && this.campaign.turnover_type !== "hf" && this.campaign.turnover_type !== "ghf" && this.campaign.turnover_type !== "natural_discount") {
          this.campaign.turnover_type = "cpc"
          if (this.campaign.cost_type) {
            this.campaign.cost_type = "cpc"
          }
        }
      }
      if (this.campaign.target_type === "budget") {
        this.campaign.turnover_value = 0
        if (this.campaign.turnover_type !== "hf" && this.campaign.turnover_type !== "ghf") {
          this.campaign.turnover_type = "hf"
        }
      }
    },
    "campaign.budget_target_type"() {
      if (!this.campaign.budget_target_type) {
        this.campaign.budget_target_value = 0
      }
    },
    "campaign.turnover_type"() {
      if (this.campaign.turnover_type === "natural_discount") return this.campaign.turnover_value = 0.0
      if (!this.campaign.turnover_value) this.campaign.turnover_value = 10.0
    },
    "campaign.cost_type"() {
      if (this.campaign.cost_type === null) {
        this.campaign.cost_value = 0
      } else {
        this.campaign.cost_value = 5.0
      }
    },
    "campaign.dummy"() {
      if (this.campaign.dummy) {
        this.campaign.turnover_budget = 0
        this.campaign.turnover_budget_type = null
        this.campaign.turnover_one_off_fee = 0
        this.campaign.include_clicks = false
        this.campaign.include_complete = false
        this.campaign.include_video_views = false
        this.campaign.include_conversions = false
        this.campaign.include_cpm = false
        this.campaign.include_cpc = false
        this.campaign.include_cpo = false
        this.campaign.include_ad_group_metrics = false
        this.campaign.include_keyword_metrics = false
        this.campaign.apply_budget_cap = false
      } else {
        this.campaign.include_clicks = !this.channel || this.channel.supports_clicks
        this.campaign.include_complete = !this.channel || this.channel.supports_complete
        this.campaign.include_video_views = !this.channel || this.channel.supports_video_views
        this.campaign.include_conversions = !this.channel || this.channel.supports_conversions
        this.campaign.include_cpm = !this.channel || this.channel.supports_cpm
        this.campaign.include_cpc = !this.channel || this.channel.supports_cpc
        this.campaign.include_cpo = !this.channel || this.channel.supports_cpo
        this.campaign.include_ad_group_metrics = !this.channel || this.channel.supports_ad_group_metrics
        this.campaign.include_keyword_metrics = !this.channel || this.channel.supports_keyword_metrics
      }
    },
    "campaign.turnover_budget"() {
      if (this.campaign.turnover_budget) {
        if (!this.campaign.turnover_budget_type) this.campaign.turnover_budget_type = "total"
      } else {
        this.campaign.turnover_budget_type = null
      }
    },
  },
}
</script>
